/**
 * Client-side PII heuristic redaction for free-text fields submitted with
 * user feedback (`comment`, `messageExcerpt`).
 *
 * Detects + masks Brazilian CPF/CNPJ and email addresses. The user can
 * opt out via the popover checkbox; the caller is responsible for honouring
 * that toggle (this lib is unconditional).
 *
 * Patterns are deliberately conservative — false negatives are preferred
 * over false positives because the redacted output is what reaches storage.
 */

/** Replacement token surfaced to admins so redaction is visible. */
export const REDACTED_TOKEN = "[REDACTED]";

// CPF: NNN.NNN.NNN-NN  (also matches the unformatted 11-digit form so users
// who type their own CPF without dots are still covered)
const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;

// CNPJ: NN.NNN.NNN/NNNN-NN
const CNPJ_RE = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g;

// Email — RFC-ish but kept permissive enough to catch obvious cases without
// over-matching urls (the trailing word boundary keeps `@example.com.` from
// eating the period).
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

export interface SanitizeResult {
  text: string;
  redactedCount: number;
}

/**
 * Redact CPF/CNPJ/email matches from the provided string.
 *
 * Returns the redacted text plus a count of matches replaced (0 means the
 * input came back untouched — useful for the UI to skip the "we redacted N
 * tokens" hint when nothing changed).
 */
export function sanitizeFeedbackText(input: string | undefined): SanitizeResult {
  if (!input) return { text: input ?? "", redactedCount: 0 };
  let count = 0;
  const replace = (re: RegExp) => (text: string) =>
    text.replace(re, () => {
      count += 1;
      return REDACTED_TOKEN;
    });
  // CNPJ runs first as a defensive measure: with the current patterns
  // CPF can't match a CNPJ string (CPF needs an 11-digit run with a
  // trailing word boundary, CNPJ has 14 unbroken digits or non-digit
  // separators in the way), but if either pattern is loosened later
  // we want the longer match to win.
  const text = [replace(CNPJ_RE), replace(CPF_RE), replace(EMAIL_RE)].reduce(
    (acc, fn) => fn(acc),
    input,
  );
  return { text, redactedCount: count };
}
